import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  IconButton,
  Stack,
  InputAdornment,
  Box,
  Fade,
  useTheme,
  useMediaQuery,
  MenuItem
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory";
import DescriptionIcon from "@mui/icons-material/Description";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";

import LogoRFID from "../imagenes/logorfid.png";

export default function EditarProductoModal({ producto, refresh }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [descripcion, setDescripcion] = useState("");
  const [adicional, setAdicional] = useState("");
  const [estado, setEstado] = useState("");
  const [estados, setEstados] = useState([]);
  const [error, setError] = useState("");

  // Cuando cambia el producto
  useEffect(() => {
    if (producto) {
      setDescripcion(producto.descripcion || "");
      setAdicional(producto.adicional || "");
      setEstado(producto.idEstado || "");
    }
  }, [producto]);

  // Obtener estados
  useEffect(() => {
    const fetchEstados = async () => {
      try {
        const res = await axios.get(process.env.REACT_APP_API_ESTADOS);
        setEstados(res.data);
      } catch (err) {
        console.error(err);
        setError("Error al cargar estados");
      }
    };
    fetchEstados();
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setError("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!descripcion || !adicional || !estado) {
      setError("Completa todos los campos");
      return;
    }

    try {
      setLoading(true);
      await axios.post(process.env.REACT_APP_API_PRODUCTO_EDITAR, {
        id: producto.idProductos,
        descripcion,
        adicional,
        estado
      });

      handleClose();
      if (refresh) refresh();
    } catch (err) {
      console.error(err);
      setError("Error al actualizar producto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* BOTÓN EDITAR */}
      <IconButton
        onClick={handleOpen}
        sx={{
          background: "#f5f5f5",
          "&:hover": { background: "#e0e0e0" }
        }}
      >
        <EditIcon />
      </IconButton>

      {/* MODAL */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, overflow: "hidden" } }}
      >
        {/* HEADER MODERNO */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: { xs: 2, md: 3 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(90deg, #1976d2, #42a5f5)",
            color: "white"
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Editar Producto
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Actualiza la información del producto
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* FORMULARIO */}
        <form onSubmit={handleSave}>
          <DialogContent dividers sx={{ p: { xs: 2, sm: 3, md: 4 }, overflowY: "visible" }}>
            <Grid container spacing={4}>
              {/* IZQUIERDA: FORM */}
              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  {error && <Alert severity="error">{error}</Alert>}

                  <TextField
                    label="Descripción"
                    fullWidth
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    label="Adicional"
                    fullWidth
                    value={adicional}
                    onChange={(e) => setAdicional(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AddIcon />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    select
                    label="Estado"
                    fullWidth
                    value={estado}
                    onChange={(e) => setEstado(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InventoryIcon />
                        </InputAdornment>
                      )
                    }}
                  >
                    {estados.map((item) => (
                      <MenuItem key={item.idEstado} value={item.idEstado}>
                        {item.estado}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="EPC"
                    fullWidth
                    value={producto.epc}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AssignmentIcon />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    label="Código"
                    fullWidth
                    value={producto.codigobarra}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AssignmentIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Stack>
              </Grid>

              {/* DERECHA: PANEL INFO */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    background: "#f8fafc",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    textAlign: "center"
                  }}
                >
                  <img src={LogoRFID} alt="logo" style={{ width: 100, opacity: 0.9 }} />
                  <Typography mt={2} fontWeight={500}>
                    Información del Producto
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Mantén los datos actualizados para un mejor control del sistema.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* BOTONES */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-end" mt={4}>
              <Button
                fullWidth={isMobile}
                onClick={handleClose}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Cancelar
              </Button>

              <Button
                fullWidth={isMobile}
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  background: "linear-gradient(90deg, #1976d2, #42a5f5)"
                }}
              >
                {loading ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </DialogContent>
        </form>
      </Dialog>
    </>
  );
}